import React from 'react';
import { FEATURE_CONFIG } from '../../../services/patternClustering.service';

/**
 * BehavioralFeaturesCard - Displays the 8 behavioral features for a subject
 */
const BehavioralFeaturesCard = ({ features, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
        <div className="space-y-2">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-6 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!features) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Behavioral Features</h3>
        <div className="text-center py-4 text-gray-500 text-sm">
          <p>No behavioral features available.</p>
          <p className="text-xs mt-1">Extract features to see behavioral metrics.</p>
        </div>
      </div>
    );
  }

  // Feature keys in display order
  const featureKeys = [
    'avgDailyDistanceMiles',
    'uniqueLocationsPerWeek',
    'routineAdherenceScore',
    'nightActivityRatio',
    'weekendActivityRatio',
    'highRiskZoneAffinity',
    'velocityVariance',
    'dwellTimeEntropy',
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Behavioral Features</h3>

      <div className="space-y-3">
        {featureKeys.map((key) => {
          const config = FEATURE_CONFIG[key];
          const value = features[key];
          const formatted = config.format(value);
          const isPercentage = config.unit === '%';
          const barWidth = isPercentage ? Math.min(parseFloat(formatted), 100) : Math.min((value || 0) / 10 * 100, 100);

          return (
            <div key={key}>
              <div className="flex justify-between items-center mb-1">
                <div className="text-xs text-gray-600" title={config.description}>
                  {config.label}
                </div>
                <div className="text-sm font-medium">
                  {formatted}{config.unit}
                </div>
              </div>
              {isPercentage && (
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${barWidth}%` }}
                  ></div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Timestamp */}
      {features.extractionTimestamp && (
        <div className="mt-4 text-xs text-gray-400 text-right">
          Extracted: {new Date(features.extractionTimestamp).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default BehavioralFeaturesCard;
